/**
 * Role / KeyRing constants, plus the helpers that read them.
 *
 * Since API-464 the login / authorize / refresh body carries `roles` on
 * `AuthenticatedResponse`. Session hydration reads that list (see `auth-user.ts`). The access
 * token is encrypted (JWE) and is not decoded on the client.
 *
 * IMPORTANT — keyring conflation (M1.1a, ROBO-234). The API's `RoleResolverService.ResolveAsync`
 * flattens the BRAND keyring and the USER keyring into ONE role list:
 *
 *     if (sBrandKeyRing.HasValue()) roles.AddRange(sBrandKeyRing.Split(","));
 *     if (sUserKeyRing.HasValue())  roles.AddRange(sUserKeyRing.Split(","));
 *
 * Legacy keeps them apart — `HasKey(x)` reads the USER keyring, `HasKeyEx(User.BrandKeyRing, x)`
 * reads the BRAND keyring. A role in `user.roles` therefore cannot tell you which keyring it came
 * from, so every client-side gate below that legacy sources from one specific keyring is looser
 * than legacy by exactly that conflation. Where exact parity matters the flag has to come back
 * from the server (`GET dashboards/header-stats` and `GET dashboards/markets` already do this for the header
 * stats and the market selector).
 *
 * ★ PARTIALLY OUT OF DATE (2026-08-16, ROBO-321). "Cannot tell you which keyring it came from" is
 * no longer strictly true for USER keys: `RoleResolverService.cs:52` also emits a `user:`-prefixed
 * copy of each one, and {@link hasUserRole} reads them. It IS still true for BRAND keys, which are
 * only ever emitted bare — so a brand-sourced gate (`HasKeyEx`) remains unfixable this way and the
 * server-flag advice above still stands for those.
 *
 * The `user:` route became trustworthy with API `604bb8d5` (ROBO-289), which made every login path
 * populate the user keyring — before that, `RefreshTokenAsync` passed none at all.
 */

/** `HDRoles.IS_PARENTBRAND` — added by `RoleResolverService.cs:41` when the brand is a parent. */
export const ROLE_IS_PARENT_BRAND = 'IsParentBrand';

/** `HDRoles.IS_CHILDBRAND` — added by `RoleResolverService.cs:43`. */
export const ROLE_IS_CHILD_BRAND = 'IsChildBrand';

/**
 * Legacy `HasKey("dash-teammode")` (USER keyring; in OWNERKEYS for user types < 3).
 *
 * ★ WHERE PARITY WITH LEGACY MATTERS, READ IT `user:`-PREFIXED WITH {@link hasUserRole}. Legacy's
 * `HasKey` resolves `User("sKeyRing")` — the caller's OWN ring (`inc/functions.asp:1103-1113`) — but
 * the bare claim is the BRAND and USER keyrings flattened into one list (see the note at the top of
 * this file). A bare `dash-teammode` therefore also matches a brand-level grant legacy would not
 * honour, which is the ROBO-130 escalation class: one key on the brand ring would promote every
 * team member in that brand. {@link canEditInvoiceFields} (UX-403) reads it prefixed for exactly
 * this reason. The older dashboard-audience gates still use {@link hasRole} and are looser by that
 * conflation — that is pre-existing, not a pattern to copy.
 */
export const KEY_DASH_TEAM_MODE = 'dash-teammode';

/** Legacy `HasKeyEx(User.BrandKeyring, "dash-tmsimple")` (BRAND keyring). */
export const KEY_DASH_TM_SIMPLE = 'dash-tmsimple';

/** Legacy `HasKey("dash-notsimple")` (USER keyring) — per-user opt-out of simple mode. */
export const KEY_DASH_NOT_SIMPLE = 'dash-notsimple';

/** Legacy `HasKey("dashcomm")` (USER keyring) — Commission Queue tile. */
export const KEY_DASH_COMM = 'dashcomm';

/** Legacy `HasKey("nav-clients")` (USER keyring). */
export const KEY_NAV_CLIENTS = 'nav-clients';

/**
 * "Add Team Members" in the permissions UI — "Add new team members for this business account".
 * `Brand/permissions.asp:149`.
 */
export const KEY_TEAM_CREATE = 'team-create';

/**
 * "Team Member Login" — "Access the accounts of other team members" (`Brand/permissions.asp:148`).
 *
 * Also what a TEAM MEMBER needs to see the roster in the side nav at all (user decision,
 * 2026-09-04) — see `canShowNavTeamMembers`. Reading a colleague's record and being able to act as
 * them are the same permission in legacy's model, and this key is the one that says so.
 */
export const KEY_TEAM_LOGIN = 'team-login';

/**
 * HD staff access. Legacy gates its own Admin area on `User("nUserTypeID") = "1" and
 * HasKey("admin")` (`inc/begin.asp:189, 208, 304`) — the user type alone is not enough, and this is
 * the key that finishes it.
 *
 * ★ A USER key, read with `hasUserRole`. `HasKey` resolves `User("sKeyRing")`
 * (`inc/functions.asp:1103`), i.e. the user's own ring and not the brand's, so the bare claim would
 * widen it to everyone in a brand that holds it — the ROBO-12 conflation.
 */
export const KEY_ADMIN = 'admin';

/**
 * ROBO-357. "Allow Direct Login" (`Admin/Data/keyrings.asp:112`) — impersonate WITHOUT recording a
 * super user, so the session is indistinguishable from that user having logged in themselves.
 *
 * ★ A USER key. Read it with `hasUserRole`, never `hasRole`. Legacy's gate is
 * `HasKey("directlogin")`, which reads the USER keyring only; the bare key would also match a
 * BRAND-level grant and silently widen this to everyone in that brand — see the note on
 * {@link USER_KEY_PREFIX}.
 */
export const KEY_DIRECT_LOGIN = 'directlogin';

/*
 * `dashtickle` / `dashtickle-all` are deliberately absent. Prospect/CRM functionality is out of
 * scope for the rebuild (user decision, 2026-08-04) — do not add them back.
 */

/** Legacy `HasKeyEx(BrandKeyRing, "parent-support")` (BRAND keyring) — M1.1v. */
export const KEY_PARENT_SUPPORT = 'parent-support';

/**
 * Legacy `HasKeyEx(User.BrandKeyRing, "acctfunds")` (BRAND keyring) — "Allow On Account Funds to be
 * Redeemed" (`Brand/defaults.asp:1149`). Also the alternative gate on Pay Invoices / Bulk Payment
 * (`inc/begin.asp:487`), since funds on account can settle invoices with no card processor.
 */
export const KEY_ACCOUNT_FUNDS = 'acctfunds';

/** Legacy `HasKeyEx(User.BrandKeyRing, "site-disablepropertysite")` (BRAND keyring). */
export const KEY_SITE_DISABLE_PROPERTY_SITE = 'site-disablepropertysite';

/** Legacy `HasKeyEx(User.BrandKeyRing, "markets")` (BRAND keyring) — market selector gate. */
export const KEY_BRAND_MARKETS = 'markets';

/** Legacy `HasKeyEx(User.BrandKeyRing, "orderfinalize")` (BRAND keyring) — invoice finalizing. */
export const KEY_ORDER_FINALIZE = 'orderfinalize';

/**
 * USER keyring — the user has opted in to landing here rather than on the Classic ASP site at
 * login. Set from the profile menu via `PATCH team-members/{id}/keyrings` on the user's own id; read with
 * {@link hasUserRole}, never {@link hasRole}, because it is a per-user preference and a brand-level
 * key of the same name must not switch it on for everyone.
 *
 * Legacy honours it at login: `UserLandingPath` / `UserLandingSegment` in
 * `inc/functions_security.asp` send a user holding the key on their OWN keyring to the studio shell
 * instead of `/Dashboard/`, unless the login carried an explicit return URL.
 */
export const KEY_UX_DEFAULT = 'ux-default';

/**
 * USER keyring — "Hubby" on the Team Member Permissions tab (user decision 2026-09-20). Owners and
 * Team Members have Hubby by default; this keeps it for one of them on a brand with
 * {@link KEY_NO_HUBBY}. Admins always have Hubby and never need it.
 *
 * ★ READ IT WITH {@link hasUserRole}, NEVER {@link hasRole}. It is a per-user grant, and the bare
 * claim is emitted from the BRAND keyring too (see the note at the top of this file) — so `hasRole`
 * would hand Hubby to every user in a brand whose ring happens to carry the key, which is the
 * ROBO-12 conflation. {@link useCanUseHubby} is the one read; do not re-derive it at a call site.
 *
 * ★ COSMETIC, LIKE EVERY GATE IN THIS FILE. Hubby's admin-only-ness has never been enforced:
 * ai-router accepts any valid session JWT and does not look at user type, and what actually bounds
 * a Hubby session is the authorization on each API tool it calls. The key decides what is OFFERED.
 */
export const KEY_HUBBY_ENABLE = 'hubby-enable';

/**
 * USER keyring — "Create Hubby Actions" on the Team Member Permissions tab (UX-951). Gives an Owner
 * or Team Member who has Hubby the Hubby Actions page. Without it they
 * still have Hubby, and still run the actions they are on from the slash command list.
 *
 * ★ A NEW NAME, NOT THE RETIRED `hubby-actions` (ROBO-499, retired by ROBO-543). Reusing that one
 * would silently hand the page to everyone whose keyring still carries the old key.
 *
 * ENFORCED since API-778 (`HubbyActionRules.CanManage`). Read it with {@link hasUserRole}, like
 * `hubby-enable`.
 */
export const KEY_HUBBY_ACTIONS_CREATE = 'hubby-actions-create';

/**
 * BRAND keyring — "Disable Hubby" in the admin Subtractions section. Takes Hubby and Hubby Actions
 * away from the brand's Owners and Team Members, except anyone holding {@link KEY_HUBBY_ENABLE}.
 *
 * ★ READ IT WITH {@link hasBrandRole}, like any restriction a user keyring must not be able to set.
 */
export const KEY_NO_HUBBY = 'nohubby';

/**
 * Legacy `HasKeyEx(User.BrandKeyRing, "15min")` (BRAND keyring) — the shoot picker's minute step
 * (`dashboard3.asp:286`). 15 minutes with the key, 30 without.
 */
export const KEY_FIFTEEN_MINUTE_SHOOTS = '15min';

/**
 * Legacy `HasKey(sBrandKeyRing, "client-disablewho")` (BRAND keyring) — the brand hides which
 * provider is shooting a property from its clients (M1.1h/M1.1i confirmation history).
 */
export const KEY_CLIENT_DISABLE_WHO = 'client-disablewho';

/**
 * `HDRoles.CART` / `HDRoles.CART_MODIFY` — the two keyring roles that let a non-Admin, non-Owner
 * reach `POST orders/{orderId}/tasks/{taskId}/assignment` (M1.1h/M1.1i shoot-date write).
 */
export const KEY_CART = 'cart';
export const KEY_CART_MODIFY = 'cart-modify';

/**
 * API-664. Legacy `nPermSelfComplete` (`Sites/summary.asp:974`): the BRAND key `order-doneself` limits a team
 * member to completing its own tasks; the USER keys `order-doneothers` and `dash-teammode` lift that.
 */
export const KEY_ORDER_DONE_SELF = 'order-doneself';
export const KEY_ORDER_DONE_OTHERS = 'order-doneothers';

/** API-664. Legacy `HasKey("buycredits")` (`Account/purchase.asp:578`) — a team member may buy credits. */
export const KEY_BUY_CREDITS = 'buycredits';

/**
 * `HDRoles.BRAND_REPORTS` — the Reports gate, `reports.asp:10` and every report that shares it.
 *
 * ★ READ IT WITH {@link hasUserRole}, NEVER {@link hasRole}. Legacy's `HasKey("brand-reports")`
 * reads the caller's OWN keyring; the bare claim is emitted from the brand keyring too, so a
 * `hasRole` check also admits every user in a brand that holds the key — the ROBO-12 bug class
 * the API side spells out on `CartSectionAccessAuthorizationHandler`.
 */
export const KEY_BRAND_REPORTS = 'brand-reports';

/**
 * `HDRoles.BRAND_APPS` — the brand Integration Hub gate, legacy `Brand/apps.asp:14`.
 *
 * ★ READ IT WITH {@link hasUserRole}, NEVER {@link hasRole} — the same ROBO-12 reason as
 * {@link KEY_BRAND_REPORTS}. And only on the Team Member arm: `brand-apps` is in legacy's
 * `OWNERKEYS`, so an Admin and an Owner hold it implicitly, while the `user:` claims carry
 * `tblUser.sKeyRing` alone. See `canAccessBrandIntegrationHub`.
 */
export const KEY_BRAND_APPS = 'brand-apps';

/** Comma-list membership in legacy is a substring test; here it is an exact role match. */
export function hasRole(roles: string[] | undefined, role: string): boolean {
    return Array.isArray(roles) && roles.includes(role);
}

/**
 * `KeyRings.USER_KEY_PREFIX` — `RoleResolverService.cs:52` emits a `user:`-prefixed copy of every
 * USER-keyring key alongside the bare one, precisely so the conflation described at the top of this
 * file can be undone where it matters.
 */
export const USER_KEY_PREFIX = 'user:';

/**
 * The client twin of the API's `ClaimsExtensions.HasUserKey` (`ClaimsExtensions.cs:34`) — reads the
 * USER keyring alone, which is what legacy's `HasKey(x)` does. {@link hasRole} cannot: the bare key
 * is emitted from BOTH keyrings, so it also matches a brand-level grant that legacy would not have
 * accepted.
 *
 * ★ SAFE AS OF API `604bb8d5` (ROBO-289), AND NOT BEFORE. Prefixed keys are only as reliable as
 * the login path that minted the token, and the API used to be inconsistent — `RefreshTokenAsync`
 * passed no user keyring at all, so a refreshed token carried zero `user:*` roles for every user of
 * every type. All nine call sites now pass `x.sKeyRing.WithOwnerDefaults(type)`. A token minted
 * before that deploy still lacks the claims until it refreshes.
 *
 * ★ THIS DOES NOT WORK FOR BRAND KEYS, AND NEVER WILL. Only user keys get a prefixed copy; there is
 * no `brand:` counterpart. A gate that legacy sources with `HasKeyEx(User.BrandKeyRing, ...)` must
 * stay on {@link hasRole} and remains looser than legacy — see `canShowNavFees`, which deliberately
 * uses one helper per arm for exactly this reason.
 */
export function hasUserRole(roles: string[] | undefined, key: string): boolean {
    return hasRole(roles, USER_KEY_PREFIX + key);
}

/**
 * `KeyRings.BRAND_KEY_PREFIX` — since API-662 `RoleResolverService` emits every BRAND-keyring key with
 * this prefix, and `AuthenticatedResponse.roles` carries it alongside the bare compatibility copy.
 */
export const BRAND_KEY_PREFIX = 'brand:';

/** The client twin of the API's `ClaimsExtensions.HasBrandKey` — reads the BRAND keyring alone. */
export function hasBrandRole(roles: string[] | undefined, key: string): boolean {
    return hasRole(roles, BRAND_KEY_PREFIX + key);
}

/**
 * Legacy `nBrandIsParent = User.BrandIsParent()`. Derived from the `IsParentBrand` role on the
 * auth response (API-464), not by decoding the access token.
 */
export function deriveIsParentBrand(roles: string[] | undefined): boolean {
    return hasRole(roles, ROLE_IS_PARENT_BRAND);
}

/** Sibling of {@link deriveIsParentBrand}; legacy `User.BrandIsChild()`. */
export function deriveIsChildBrand(roles: string[] | undefined): boolean {
    return hasRole(roles, ROLE_IS_CHILD_BRAND);
}
